
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Modal } from "react-bootstrap";
import { QueryListProvider } from "../../../../component/solution/QueryTableList/QueryListContext";
import { QueryTableList } from "../../../../component/solution/QueryTableList/QueryTableList";
import { IntegrationStatus, IQuerySelectData } from "../../../../component/solution/Interfaces";
import { AlertTriangle, Ban, Bell, CheckCircle, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { useAuth } from "../../../../contexts/AuthContext";


export interface IFailedTransaction {
    m24l_reference: string,
    function_call_id: string, 
    extended_message: string | null, 
    completed_at: string,
    created: string, 
}


export const StockAdjustments: React.FC = () => {
 
    const { fetchData, userDetail, setRefreshTable } = useAuth();
    const navigate = useNavigate();

    const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
    
    const defaultSelectedFailedTransaction = { m24l_reference: "", function_call_id: "", extended_message: "", completed_at: "",  created: "" }; 
    const [failedTransactionsData, setFailedTransactionsData] = useState<IFailedTransaction[]>([]);
    const [selectedFailedTransaction, setSelectedFailedTransaction] = useState<IFailedTransaction>(defaultSelectedFailedTransaction);
    const [failedTransactionsRerunModal, setFailedTransactionsRerunModal] = useState<boolean>(false);
    const [failedTransactionsHandledModal, setFailedTransactionsHandledModal] = useState<boolean>(false);
    const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
    const [expanded, setExpanded] = useState(false);
    
    // -> failed transactions -> stockAdjustments
    const defaultQueryStateFailedTransactions: IQuerySelectData = {
            appCode: "ares",
            selectCode: "sa-m24l-stock-adjustment",  
            where: `handled_status IS NOT DISTINCT FROM :handled_status`,  
            params: {'handled_status': "REQUIRED"}
    }; 
   const [querySelectStateFailedTransaction, setQuerySelectStateFailedTransaction] = useState<IQuerySelectData>(defaultQueryStateFailedTransactions);

    const [showCancelTransactionModal, setShowCancelTransactionModal] = useState<boolean>(false);
    const [cancelledTransactionData,  setCancelledTransactionData] = useState<{ 
            user_id: string, 
            document_number: string, 
            document_type: string, 
            transaction_number: string, 
            title: string
    } | null>(null); 


    useEffect(() => {
            //userDetail?.roles?.includes("store_management") || userDetail?.roles?.includes("HQ")
            if (userDetail?.roles) {
                fetchData(queryUrlSelectNoCount, { method: "POST", body: JSON.stringify(querySelectStateFailedTransaction) }, processDataFailedTransaction);
            }
    }, [userDetail?.roles, submitRefreshData]); // expanded
   
    
   
    // failed transactions for stock adjustment
    const processDataFailedTransaction = (result: any) => {
   
           const failedTransactions: IFailedTransaction[] = [];
   
           for (const key in result?.data) {
               const item = result.data[key];
               if (item?.data) {
                   failedTransactions.push(item.data as IFailedTransaction);
               }
           }
           
           failedTransactions.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
       
           // Collapse table secion if empty reset expand
           if (failedTransactions.length === 0) {
               setExpanded(false);
           }
       
           setFailedTransactionsData(failedTransactions);
    }

     //handle sumbit for stock adjustment rerun 
    const handleFailedTransactionRerunSubmit = () => {
        if(selectedFailedTransaction?.m24l_reference !== "" && selectedFailedTransaction?.function_call_id !== ""){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedFailedTransaction?.function_call_id}/rerun`, 
            {method:"POST", body: JSON.stringify({ })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                    setSubmitRefreshData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                }
            });
        } else {
            return; 
        }
    }
    
    
    //handle sumbit for stock adjustment handled
    const handleFailedTransactionHandledSubmit = () => {
            if(selectedFailedTransaction?.m24l_reference !== "" && selectedFailedTransaction?.function_call_id !== ""){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedFailedTransaction?.function_call_id}/handle`, 
            {method:"POST", body: JSON.stringify({ })}, 
            (result: any, status: string) =>  { 
                if(status === "200"){
                    setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                    setSubmitRefreshData(prev => !prev); //trigger refresh on success 
                }else{
                    setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                }
            });
        } else {
            return; 
        }
    }
    
    
    const FailedTransactionsHeader = (
        failedTransactionsData.length > 0 && 
        <button className="btn p-1 d-flex align-items-center justify-content-center" title={expanded ? "Hide failed transactions" : "Show failed transactions"} onClick={() => setExpanded(prev => !prev)} 
            style={{ border: "1px solid #c10000", background: "transparent", borderRadius: "50%", color: "#c10000", width: "22px", height: "22px"}}
        >
            {expanded ? ( <ChevronUp size={20} strokeWidth={2.2} /> ) : ( <ChevronDown size={20} strokeWidth={2.2} />)}
        </button>
    );
    

  
    const thNewStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        borderTop: "1px solid #dee6ed",
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const tdStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };

    
    const FailedTransactionsSection: React.FC<{ 
   
           data: IFailedTransaction[];
           expanded: boolean;
           toggleExpanded: () => void;
   
           selectedTransaction: IFailedTransaction;
           showRerunModal: boolean;
           showHandledModal: boolean;
   
           onRerunClick: (tx: IFailedTransaction) => void;
           onHandledClick: (tx: IFailedTransaction) => void;
           onRerunConfirm: () => void;
           onHandledConfirm: () => void;
           onRerunCancel: () => void;
           onHandledCancel: () => void;
   
       }> = ({
   
           data,
           expanded,
           selectedTransaction,
           showRerunModal,
           showHandledModal,
           onRerunClick,
           onHandledClick,
           onRerunConfirm,
           onHandledConfirm,
           onRerunCancel,
           onHandledCancel,
   
       }) => {
           if (!data?.length) return null;
   
           return (
               <>
                   {expanded && (
                       <div style={{border: "1px solid #c10000" , padding: '0px 4px', marginBottom: '14px', marginTop: '14px', borderRadius: '5px'}}>
                            <h5 className="mt-2 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                               Failed Transactions: 
                           </h5>
                           <div style={{
                               maxHeight:failedTransactionsData.length > 10 ? "450px" : "unset", 
                               overflowY: failedTransactionsData.length > 10 ? "auto" : "visible",
                               marginBottom: "4px",
                               position: "relative",
                               backgroundColor: "#fff", 
                               borderBottom: "1px solid #dee6ed",
                           }}
                           >
                           <table className="table table-bordered  mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                               <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa", boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.05)"}}>
                                   <tr>
                                       <th style={{ ...thNewStyle, width: "160px"}}>M24L Reference</th>
                                       <th style={thNewStyle}>Message</th>
                                       <th style={thNewStyle}>Last Run</th>
                                       <th style={{ ...thNewStyle, width: "100px"}}> Actions</th>
                                   </tr>
                               </thead>
                               <tbody>
                               {data.map((tx, index) => (
                                   <tr key={index}>
                                       <td style={tdStyle}>{tx.m24l_reference}</td>
                                       <td title={tx.extended_message ?? ""} style={{...tdStyle, maxWidth: "300px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",}} >
                                           {tx.extended_message}
                                       </td>
                                       <td style={tdStyle}>{formatDateFull(tx.completed_at ?? "")}</td>
                                       <td style={{...tdStyle}} className="d-flex gap-1">
                                           <button className="btn btn-secondary d-flex align-items-center gap-1" title="Rerun" onClick={() => onRerunClick(tx)}>
                                               <RotateCcw  style={{ width: "17px", height: "17px" }}/>
                                           </button>
                                           <button className="btn btn-secondary d-flex align-items-center gap-1" title="Handled" onClick={() => onHandledClick(tx)}>
                                               <CheckCircle style={{ width: "17px", height: "17px" }} />
                                           </button>
                                       </td>
                                   </tr>
                                   )) }
                               </tbody>
                           </table>
                       </div>
                       <div style={{marginBottom: "3px", marginLeft: "10px", color: "#c10000" }}>
                           <small className="text-muted fw-bolder">Total Failed Transactions: {data.length}</small>
                       </div>                       
                    </div>
                   )}
   
                   {/* Rerun Modal */}
                   <Modal show={showRerunModal} onHide={onRerunCancel}>
                       <Modal.Header closeButton>
                           <Modal.Title>{`Rerun Failed [${selectedTransaction?.m24l_reference}] Transaction`}</Modal.Title>
                       </Modal.Header>
                       <Modal.Body>
                           <p>Are you sure you would like to rerun this transaction?</p>
                       </Modal.Body>
                       <Modal.Footer>
                           <Button variant="primary" onClick={onRerunConfirm}>Confirm</Button>
                           <Button variant="secondary" onClick={onRerunCancel}>Cancel</Button>
                       </Modal.Footer>
                   </Modal>
   
                   {/* Handled Modal */}
                   <Modal show={showHandledModal} onHide={onHandledCancel}>
                       <Modal.Header closeButton>
                           <Modal.Title>{`Handle Failed [${selectedTransaction?.m24l_reference}] Transaction`}</Modal.Title>
                       </Modal.Header>
                       <Modal.Body>
                           <p>Are you sure you would like to mark this transaction as handled?</p>
                       </Modal.Body>
                       <Modal.Footer>
                           <Button variant="primary" onClick={onHandledConfirm}>Confirm</Button>
                           <Button variant="secondary" onClick={onHandledCancel}>Cancel</Button>
                       </Modal.Footer>
                   </Modal>
               </>
           );
       };
   
       const FailedTransactionsBody = (
           <FailedTransactionsSection
               data={failedTransactionsData}
               expanded={expanded}
               toggleExpanded={() => setExpanded(prev => !prev)}
               selectedTransaction={selectedFailedTransaction}
               showRerunModal={failedTransactionsRerunModal}
               showHandledModal={failedTransactionsHandledModal}
               //handle stock adjustment rerun click
               onRerunClick={(tx) => {
                   setSelectedFailedTransaction(tx);
                   setFailedTransactionsRerunModal(true);
               }}
               //handle stock adjustment handle click
               onHandledClick={(tx) => {
                   setSelectedFailedTransaction(tx);
                   setFailedTransactionsHandledModal(true);
               }}
               //handle sumbit for stock adjustment rerun [SUBMIT]
               onRerunConfirm={() => {
                   handleFailedTransactionRerunSubmit();
                   setFailedTransactionsRerunModal(false);
               }}
               ////handle sumbit for stock adjustment handled [SUBMIT]
               onHandledConfirm={() => {
                   handleFailedTransactionHandledSubmit();
                   setFailedTransactionsHandledModal(false);
               }}
               //handle stock adjustment rerun reset click [RESET]
               onRerunCancel={() => {
                   setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                   setFailedTransactionsRerunModal(false);
               }}
               //handle stock adjustment handled reset click [RESET]
               onHandledCancel={() => {
                   setSelectedFailedTransaction(defaultSelectedFailedTransaction);
                   setFailedTransactionsHandledModal(false);
   
               }}
           />
       );
   


    /* 
        Status 

        Stock Adjustments
        TO_PROCESS - Pending stock adjustment message received from M24L.
        PROCESSED - Stock adjustment message prcessed in SAP.
        ERROR - The was an error processing the stock adjustment.

    */

    /* 
      [Table Rows & Order To Display]: 
        m24l_reference  
        transaction_num  
        item_code  
        from_zone  
        to_zone  
        status  
        qty  
        reason_desc  
        created  
        updated  
    */

    //CANCEL TRASCATION SECTION 
    const openModalValidTransaction = (user_id: string, document_number: string, document_type: string, transactionNumber: string, title: string) => {
        if (user_id === "" || document_number === "" || document_type === "") return;
        setCancelledTransactionData({ user_id: user_id, document_number: document_number, document_type: document_type, transaction_number: transactionNumber, title: title}); 
        setShowCancelTransactionModal(true); 
    }

    const getStatusColor = (status: string): { backgroundColor: string; color: string; } | {} => {
        if (IntegrationStatus[status as keyof typeof IntegrationStatus]) {
            return { backgroundColor: IntegrationStatus[status as keyof typeof IntegrationStatus], color: 'white' };
        }
        return {};
    };

    //handle canceld transaction
    const handleCancelTransaction = (user_id: string, document_number: string, document_type: string, transaction_number: string) => {

        if (user_id === "" || document_number === "" || document_type === "") return;
 
        // form data to be posted
        const formData  = { 
            user_id: user_id, 
            document_number: document_number, 
            document_type: document_type, 
            transaction_number: transaction_number 
        };

        fetchData(`${process.env.REACT_APP_BASE_URL}/functions/CANCEL-DOCUMENT/run`, 
        {method:"POST", body: JSON.stringify(formData)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
              setRefreshTable(prev => !prev);
            }
        });
    }

    const clearCancelTransactionModal = () => {
        setShowCancelTransactionModal(false); 
        setCancelledTransactionData(null);
    }

    return(
        <React.Fragment>
            <Container fluid className="p-0">
            <QueryListProvider>
                    <QueryTableList
                        listTitle={"Stock Adjustments - Activity Status"}
                        recordTitle={"Stock Adjustments - Activity Status"}
                        tableName={"sa_m24l_stock_adjustment"}
                        editTableName={"sa_m24l_stock_adjustment"}
                        editHasCompoundKey={false}
                        hasIntegrationStatusLegend={true}
                        errorCategoryName={'StockAdjustment'}
                        integrationstatusLegend={[
                            { status: 'ERROR', color: '#bf3636', description: 'The was an error processing the stock adjustment.'},
                            { status: 'CANCELLED', color: '#8b8b8c', description: 'Document has been cancelled.'},
                            { status: 'TO_PROCESS', color: '#d4a53f', description: 'Pending stock adjustment message received from M24L.'},
                            { status: 'PROCESSED', color: '#3ec76b', description: 'Stock adjustment message prcessed in SAP.'}
                        ]}
                         modal={
                            {
                              modalProptBy: "m24l_reference",
                              tableName: "sa_m24l_stock_movement_out",
                              modalHasViewing: true,
                            }
                        }
                        showFailedTransactionsTable={true}
                        query={{
                            appCode: "ares",
                            selectCode: "stock-adjustment-notification-projection",
                            tableName: null,
                            where: "",
                            orderBy: "",
                            pageNum: 0,
                            pageSize: 100,
                            params: {},
                        }}
                        orderColumns={[
                            { name: "m24l_reference", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "transaction_num", type: "INTEGER", sortable: true, searchable: true },
                            { name: "item_code", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "from_zone", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "to_zone", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "status", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "qty", type: "INTEGER", sortable: true, searchable: true },
                            { name: "reason_desc", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "created", type: "TIMESTAMP", sortable: true, searchable: false },
                            { name: "updated", type: "TIMESTAMP", sortable: true, searchable: false }
                        ]}
                        reportOptions={{ 
                            reportQuery: {
                              appCode: null,
                              selectCode: null,
                              tableName: "sa_m24l_stock_adjustment", 
                              where: "",
                              orderBy: "",
                              pageNum: 0,
                              pageSize: 0,
                              params: {},
                            },
                            templateName:  "apps/ares/stock_adjustment_template.xlsx", 
                            reportName: "stock_adjustment_report.xlsx",
                            reportData: null
                        }}
                        renderRowData={(row) => {

                        const tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };
                        const statusColor = getStatusColor(row.status); 
                        const tdEllipsis: React.CSSProperties = {...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}

                        return (
                            <>
                                <td style={tdStyle} title={row.m24l_reference}>{row.m24l_reference}</td>
                                <td style={tdStyle} title={row.transaction_num}>{row.transaction_num}</td>
                                <td style={tdStyle} title={row.item_code}>{row.item_code}</td>
                                <td style={tdStyle} title={row.from_zone}>{row.from_zone}</td>
                                <td style={tdStyle} title={row.to_zone}>{row.to_zone}</td>
                                <td style={{...tdStyle,...statusColor, textAlign: "center", verticalAlign: "middle"}} title={row.status}>
                                    {row.status}
                                </td>
                                <td style={tdStyle} title={row.qty}>{row.qty}</td>
                                <td style={tdEllipsis} title={row.reason_desc}>{row.reason_desc}</td>
                                <td style={tdStyle} title={formatDateFull(String(row.created))}>{formatDateFull(String(row.created))}</td>
                                <td style={tdStyle} title={formatDateFull(String(row.updated))}>{formatDateFull(String(row.updated))}</td>
                            </>    
                        );
                    }}
                      renderActions={(row) => {

                            //checking has error notification
                            const numericRefValue = Number(row.num_notifications);
                            const shouldShowErrorButton = numericRefValue > 0 && row.integration_status !== "CLOSED";

                            //checking last updated 
                            const updatedAtValue = row.updated; 
                            const createdAtValue = row.created;
                            const baseDateStr = typeof updatedAtValue === "string" && updatedAtValue.trim() ? updatedAtValue : typeof createdAtValue === "string" && createdAtValue.trim() ? createdAtValue : null;
                            const baseDate = baseDateStr ? new Date(baseDateStr).getTime() : null;
                            const isStaleUpdate = baseDate !== null && row.integration_status !== "CLOSED" && Date.now() - baseDate > 12 * 60 * 60 * 1000; // 12 hours

                            //can cancel transaction  
                            const hasCancelPermission = userDetail?.roles?.includes("cancel_documents");
                            const invalidCancelStatuses = ["CLOSED", "CANCELLED", "ERROR", "PROCESSED"];
                            const hasStatusCancelPermission = !invalidCancelStatuses.includes(String(row.integration_status));
                            const canCancelTransaction = hasCancelPermission && row.m24l_reference && hasStatusCancelPermission;

                            return (
                                <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}>
                                 <div className="d-flex gap-2">

                                 {shouldShowErrorButton && row.m24l_reference && (
                                    <button className="btn btn-light position-relative" title="View On Error Notifications"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          navigate('/m24l/notifications-new', { state: { shipment_num: row.m24l_reference as string, category: 'StockAdjustment'}});
                                        }}
                                        style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', border:'1px solid #dbdad9' }}
                                    >
                                        <span className="position-absolute top-0 end-0" style={{ fontSize: "0.60rem" , paddingRight: '2px', color: '#f7633e', fontWeight:'600' }}>
                                            {numericRefValue}
                                        </span>
                                        <Bell style={{ width: "15px", height: "15px", color:'#343a40', position: 'absolute', top: '60%', transform: 'translateY(-6px)' }} />
                                    </button>
                                 )}

                                {isStaleUpdate && row.m24l_reference && (
                                    <button className="btn btn-light" title="Last update was over 12 hours ago"
                                        style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '1px solid #dbdad9' }}
                                    >
                                        <AlertTriangle color="#4d1302" size={16} />
                                    </button>
                                )}

 
                                {canCancelTransaction && row.m24l_reference && (
                                 <button className="btn btn-light" title="Cancel Transaction"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openModalValidTransaction( userDetail?.id ?? "", String(row.m24l_reference), 'stock_adjustment', String(row.transaction_num ?? ""), "Stock Adjustments - Activity Status")
                                        }}
                                        style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '1px solid #dbdad9' }}
                                    >
                                        <Ban color="#4d1302" size={16} />
                                 </button>
                                )}
                               </div>   
                            </td>
                            );
                    }}   
                    failedTransactionsSection={{
                        toggleHeader: FailedTransactionsHeader,
                        bodyContents: FailedTransactionsBody
                    }}
                    />
                </QueryListProvider>

                 { /* open modal cancel transaction [start] */ }
                <Modal show={showCancelTransactionModal} onHide={clearCancelTransactionModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{ cancelledTransactionData?.title }: [ { cancelledTransactionData?.document_number } ]</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div className="informational-message-section">
                            <p>
                                <strong>Note:</strong> You are about to <strong>cancel this transaction</strong>. 
                                Clicking <strong>Confirm</strong> will permanently cancel the record and any associated processes. 
                                Closing this modal or clicking <strong>Cancel</strong> will <strong>abort the cancellation</strong> and leave the transaction unchanged.
                            </p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { 
                            setShowCancelTransactionModal(false); 
                            handleCancelTransaction(
                                cancelledTransactionData?.user_id ?? "", 
                                cancelledTransactionData?.document_number ?? "", 
                                cancelledTransactionData?.document_type ?? "",
                                cancelledTransactionData?.transaction_number ?? ""
                            ); 
                            setCancelledTransactionData(null);
                        }}
                        >
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={clearCancelTransactionModal}>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                { /* open modal open modal cancel transaction [end] */ }
                
            </Container>
        </React.Fragment>
    );
}

